import { useEffect, useRef, useState } from 'react';
import { Action, AdaptiveCard } from 'adaptivecards';
import 'adaptivecards/lib/adaptivecards.css';

interface PreChatSurveyCardProps {
  survey: unknown;
  disabled?: boolean;
  onSubmit: (response: Record<string, unknown>) => void;
}

type ActionWithData = Action & { data?: unknown };
type AdaptiveInput = { id?: string; value?: unknown; isSet?: () => boolean };
type AdaptiveCardWithInputs = AdaptiveCard & { getAllInputs?: () => AdaptiveInput[] };

export function PreChatSurveyCard({ survey, disabled = false, onSubmit }: PreChatSurveyCardProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const onSubmitRef = useRef(onSubmit);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    const styleId = 'cmsp-prechat-card-style';
    if (document.getElementById(styleId)) return;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .cmsp-prechat-card .ac-adaptiveCard {
        box-sizing: border-box;
        max-width: 620px;
        width: 100% !important;
        padding: 0 !important;
        font-family: Segoe UI, system-ui, sans-serif;
      }
      .cmsp-prechat-card .ac-textBlock {
        color: #424242;
      }
      .cmsp-prechat-card .ac-input {
        box-sizing: border-box;
        width: 100%;
        max-width: 100%;
        border: 1px solid #d1d5db !important;
        border-radius: 8px !important;
        padding: 10px 12px !important;
        font: inherit !important;
        color: #242424;
      }
      .cmsp-prechat-card .ac-input:focus {
        border-color: #5b5fc7 !important;
        box-shadow: 0 0 0 2px rgba(91, 95, 199, 0.18);
        outline: none;
      }
      .cmsp-prechat-card .ac-input.ac-input-validation-failed {
        border-color: #a4262c !important;
      }
      .cmsp-prechat-card .ac-pushButton {
        min-height: 36px;
        border: 1px solid #5b5fc7;
        border-radius: 8px;
        padding: 7px 16px;
        background: #5b5fc7;
        color: #fff;
        font: inherit;
        font-weight: 700;
        cursor: pointer;
      }
      .cmsp-prechat-card .ac-pushButton:hover {
        background: #464775;
        border-color: #464775;
      }
      .cmsp-prechat-card .ac-pushButton:active {
        background: #33344a;
        border-color: #33344a;
      }
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    onSubmitRef.current = onSubmit;
  }, [onSubmit]);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    host.replaceChildren();
    setRenderError(null);
    setValidationError(null);

    try {
      const adaptiveCard = new AdaptiveCard();
      adaptiveCard.onExecuteAction = (action) => {
        // Block submit and surface an accessible error if a required field is empty. AdaptiveCards
        // only validates when the server card marks inputs isRequired; many surveys don't, leaving
        // screen-reader users with no feedback. We enforce it here from the derived required state.
        const invalid = collectEmptyRequired(hostRef.current);
        if (invalid.length > 0) {
          setValidationError(`Please complete the required field: ${invalid[0].label || 'this field'}.`);
          invalid.forEach(({ el }) => el.setAttribute('aria-invalid', 'true'));
          invalid[0].el.focus();
          return;
        }
        // Email fields (e.g. "Teams Email Account", "Contact email") render as plain text inputs with
        // no format validation, so malformed addresses were accepted (6731553/6731571). Enforce a basic
        // email shape and surface it the same accessible way as the required-field error.
        const badEmail = collectInvalidEmails(hostRef.current);
        if (badEmail.length > 0) {
          setValidationError(`Please enter a valid email address for: ${badEmail[0].label || 'this field'}.`);
          badEmail.forEach(({ el }) => el.setAttribute('aria-invalid', 'true'));
          badEmail[0].el.focus();
          return;
        }
        setValidationError(null);
        onSubmitRef.current(buildPreChatResponse(action as ActionWithData, adaptiveCard));
      };
      adaptiveCard.parse(decodeSurveyText(survey));
      const renderedCard = adaptiveCard.render();
      if (renderedCard) {
        host.appendChild(renderedCard);
        // AdaptiveCards renders labels as standalone TextBlocks with no programmatic association to
        // the inputs, so every field is an unnamed "edit text" to a screen reader. Associate each
        // input with its preceding label and mirror the visual "*" required marker programmatically.
        enhanceCardAccessibility(renderedCard);
      } else {
        setRenderError('The pre-chat survey could not be rendered.');
      }
    } catch (error) {
      setRenderError((error as Error).message);
    }

    return () => host.replaceChildren();
  }, [survey]);

  return (
    <section style={styles.panel} aria-disabled={disabled}>
      <div style={styles.heading}>Before I connect you</div>
      <div style={styles.caption}>Please complete this short pre-chat survey so the live agent has the right context.</div>
      <div ref={hostRef} role="form" aria-label="Pre-chat survey" className="cmsp-prechat-card" style={disabled ? styles.disabledCard : undefined} />
      {validationError && <div role="alert" style={styles.error}>{validationError}</div>}
      {renderError && <div style={styles.error}>Sorry, this pre-chat survey cannot be shown: {renderError}</div>}
    </section>
  );
}

/**
 * Post-render accessibility pass for AdaptiveCards-rendered surveys.
 * The library emits label TextBlocks and inputs as sibling nodes with no `for`/`aria-labelledby`
 * association, so each field is announced as an unnamed "edit text". Walk the rendered card in
 * document order, remember the most recent label TextBlock, and name the next input from it.
 *
 * We set `aria-label` (not `aria-labelledby`) as the durable accessible name: AdaptiveCards runs
 * `updateInputControlAriaLabelledBy()` during its own validation/visual-state lifecycle (e.g. on
 * submit) and, because these cards use standalone TextBlocks rather than the library's native
 * `label` property, it *removes* any `aria-labelledby` we add — silently disconnecting the label the
 * first time a screen-reader user submits. `aria-label` is only touched at render time, so it
 * survives. The clean label is also stashed on `data-cmsp-label` for the validation messages.
 * A leading or trailing "*" in the label is treated as a required marker and mirrored to
 * `required`/`aria-required` so assistive tech exposes the mandatory state.
 */
function enhanceCardAccessibility(root: HTMLElement): void {
  const nodes = root.querySelectorAll<HTMLElement>('.ac-textBlock, input, textarea, select');
  let lastLabel: HTMLElement | null = null;
  nodes.forEach(node => {
    if (node.classList.contains('ac-textBlock')) {
      lastLabel = node;
      return;
    }
    // node is a form control
    if (!lastLabel) return;
    const rawText = (lastLabel.textContent || '').trim();
    if (!rawText) return;

    // Mirror the mandatory state regardless of whether the field already has an accessible name —
    // a "*" at the start or end of the label is the conventional required marker.
    if (/^\*|\*$/.test(rawText)) {
      node.setAttribute('aria-required', 'true');
      node.setAttribute('required', '');
    }

    const cleanLabel = rawText.replace(/\*/g, '').trim();
    if (!cleanLabel) return;
    // Always record the derived label for our own validation messages, independent of any ARIA the
    // library manages.
    node.setAttribute('data-cmsp-label', cleanLabel);

    // Name the control from the visible label. Respect an author-provided aria-labelledby, but
    // otherwise set aria-label directly (AdaptiveCards may have set it to the placeholder at render
    // time — the visible label is the intended, more descriptive name, so we override that).
    if (!node.getAttribute('aria-labelledby')) {
      node.setAttribute('aria-label', cleanLabel);
    }
  });
}

/** Returns required inputs that are currently empty, with their derived label text. */
function collectEmptyRequired(root: HTMLElement | null): Array<{ el: HTMLElement; label: string }> {
  if (!root) return [];
  const controls = root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
    '[aria-required="true"], [required]',
  );
  const empty: Array<{ el: HTMLElement; label: string }> = [];
  const seenRadioGroups = new Set<string>();
  controls.forEach(el => {
    const type = (el.getAttribute('type') || '').toLowerCase();

    let unsatisfied: boolean;
    if (type === 'checkbox') {
      // A required checkbox must be checked (its `value` is "on" regardless of state).
      unsatisfied = !(el as HTMLInputElement).checked;
    } else if (type === 'radio') {
      // A required radio group is satisfied when any option sharing its name is checked. Evaluate
      // the group once so we don't emit a duplicate error per option.
      const name = (el as HTMLInputElement).name;
      if (name && seenRadioGroups.has(name)) return;
      if (name) seenRadioGroups.add(name);
      const anyChecked = name
        ? !!root.querySelector(`input[type="radio"][name="${CSS.escape(name)}"]:checked`)
        : (el as HTMLInputElement).checked;
      unsatisfied = !anyChecked;
    } else {
      unsatisfied = 'value' in el ? String(el.value ?? '').trim() === '' : false;
    }

    if (unsatisfied) {
      empty.push({ el, label: deriveFieldLabel(el, root) });
    } else {
      el.removeAttribute('aria-invalid');
    }
  });
  return empty;
}

/**
 * Best-effort human label for a control, used in validation messages. Prefers the label we stashed
 * on `data-cmsp-label` (survives AdaptiveCards stripping aria-labelledby), then a live aria-label or
 * aria-labelledby target.
 */
function deriveFieldLabel(el: HTMLElement, root: HTMLElement): string {
  const stashed = el.getAttribute('data-cmsp-label');
  if (stashed) return stashed;
  const ariaLabel = el.getAttribute('aria-label');
  if (ariaLabel) return ariaLabel.replace(/\*/g, '').trim();
  const labelId = el.getAttribute('aria-labelledby');
  const labelEl = labelId ? root.querySelector(`#${CSS.escape(labelId)}`) : null;
  return (labelEl?.textContent || '').replace(/\*/g, '').trim();
}

/**
 * Returns non-empty email fields that hold a malformed address, with their derived label.
 *
 * Only single-line `<input>` controls are considered: a multiline `<textarea>` is always free text
 * (e.g. "Short description of the issue") and can never hold a single address, so it is never
 * shape-checked. A field counts as an email field when it is `type="email"`, declares an email
 * inputmode, or its label/aria-label/placeholder names an email address (word-boundary "email")
 * WITHOUT reading like a free-text prompt (e.g. "Describe your email issue" is excluded). Empty values
 * are ignored here — emptiness is the required-field validator's job — so optional email fields are
 * only checked when filled.
 */
function collectInvalidEmails(root: HTMLElement | null): Array<{ el: HTMLElement; label: string }> {
  if (!root) return [];
  const emailShape = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  // Word-boundary match so "email"/"e-mail" is caught but substrings like "emailing" are not.
  const emailWord = /\be-?mail\b/i;
  // Labels that ask for free text must not be treated as an email-address field even if they mention
  // "email" (prevents a confusing "enter a valid email address" error on a description field).
  const descriptive = /describe|description|issue|detail|comment|question|explain|problem|summary|tell us/i;
  // Only single-line inputs — textareas are excluded by selector.
  const controls = root.querySelectorAll<HTMLInputElement>('input');
  const bad: Array<{ el: HTMLElement; label: string }> = [];
  controls.forEach(el => {
    const type = (el.getAttribute('type') || '').toLowerCase();
    if (type === 'checkbox' || type === 'radio') return;

    const labelText = deriveFieldLabel(el, root);
    const ariaLabel = el.getAttribute('aria-label') || '';
    const placeholder = el.getAttribute('placeholder') || '';
    const mentionsEmail = emailWord.test(labelText) || emailWord.test(ariaLabel) || emailWord.test(placeholder);
    const isDescriptive = descriptive.test(labelText) || descriptive.test(ariaLabel) || descriptive.test(placeholder);

    const looksLikeEmail =
      type === 'email' ||
      (el.getAttribute('inputmode') || '').toLowerCase() === 'email' ||
      (mentionsEmail && !isDescriptive);
    if (!looksLikeEmail) return;

    const value = String(el.value ?? '').trim();
    if (value === '') return; // emptiness handled by the required-field validator
    if (emailShape.test(value)) {
      el.removeAttribute('aria-invalid');
      return;
    }
    bad.push({ el, label: labelText });
  });
  return bad;
}

function buildPreChatResponse(action: ActionWithData, adaptiveCard: AdaptiveCardWithInputs): Record<string, unknown> {
  const response: Record<string, unknown> = {
    Type: isRecord(action.data) && typeof action.data.Type === 'string'
      ? action.data.Type
      : 'InputSubmit',
  };

  adaptiveCard.getAllInputs?.().forEach(input => {
    if (!input.id || (input.isSet && !input.isSet())) return;
    response[input.id] = typeof input.value === 'string' ? input.value : String(input.value ?? '');
  });

  return response;
}

function decodeSurveyText(value: unknown): unknown {
  if (typeof value === 'string') return decodeHtmlEntities(value);
  if (Array.isArray(value)) return value.map(decodeSurveyText);
  if (!isRecord(value)) return value;

  return Object.fromEntries(
    Object.entries(value).map(([key, entry]) => [key, decodeSurveyText(entry)]),
  );
}

function decodeHtmlEntities(value: string): string {
  if (!value.includes('&')) return value;

  const textarea = document.createElement('textarea');
  let decoded = value;
  for (let i = 0; i < 3; i += 1) {
    textarea.innerHTML = decoded;
    const next = textarea.value;
    if (next === decoded) break;
    decoded = next;
  }
  return decoded;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

const styles = {
  panel: {
    padding: 16,
    borderTop: '1px solid #e1dfdd',
    background: '#fff',
    boxShadow: '0 -1px 5px rgba(0,0,0,0.05)',
    maxHeight: '46vh',
    overflowY: 'auto',
  } as React.CSSProperties,
  heading: {
    marginBottom: 4,
    fontSize: 14,
    fontWeight: 700,
    color: '#242424',
  } as React.CSSProperties,
  caption: {
    marginBottom: 12,
    fontSize: 12,
    color: '#616161',
  } as React.CSSProperties,
  disabledCard: {
    opacity: 0.65,
    pointerEvents: 'none',
  } as React.CSSProperties,
  error: {
    marginTop: 8,
    color: '#b91c1c',
    fontSize: 12,
  } as React.CSSProperties,
};
